// lk03 Yorqin Quvonch — Risograph-print birthday preview card.
//
// The card adopts the operator-approved "outer frame + inner card"
// structure but channels it through a Riso-print idiom: the outer
// frame is a deckle-style dual-tone wash (orange + paper) with
// registration marks at the corners; the inner cream card holds a
// circular age-medallion at its centre (the giant Yeseva-One numeral
// is the focal element, with the standard orange-paper ring around it),
// followed by name + caption.
//
// names = "Diyora · 30" — split on " · " (mid-dot canonical).
import { type LayoutCtx } from "./_shared";

const RISO_GRADIENT =
  "linear-gradient(135deg, #FDF6E3 0%, #F5C6A0 25%, #FDF6E3 50%, #E07A3C 75%, #FDF6E3 100%)";

export function BirthdayRisoPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  const [name, age] = names.split(/\s*[·•]\s*/);
  const ageNum = age?.match(/\d+/)?.[0] ?? "30";
  const captionDate = formatDate(date);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: RISO_GRADIENT,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Registration mark — top-left, riso signature */}
        <div
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            display: "flex",
            gap: 4,
            alignItems: "center",
            fontFamily: "var(--font-ui)",
            fontSize: 5,
            letterSpacing: "0.28em",
            color: ink,
            opacity: 0.75,
          }}
          aria-hidden
        >
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: accent }} />
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: ink }} />
        </div>
        <div
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            fontFamily: "var(--font-ui)",
            fontSize: 5,
            letterSpacing: "0.28em",
            color: ink,
            opacity: 0.75,
          }}
          aria-hidden
        >
          RISO · 2/2
        </div>

        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
            marginTop: 4,
          }}
        >
          TUG'ILGAN KUN
        </div>

        {/* Age medallion — giant Yeseva-One numeral in a paper ring */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: RISO_GRADIENT,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                display: "grid",
                placeItems: "center",
                boxShadow: `inset 0 0 0 1px ${accent}44`,
              }}
            >
              <div
                style={{
                  fontFamily: font,
                  fontSize: 100,
                  lineHeight: 0.78,
                  color: accent,
                  textShadow: `3px 3px 0 ${ink}`,
                  letterSpacing: "-0.03em",
                }}
              >
                {ageNum}
              </div>
            </div>
          </div>
        </div>

        {/* Ink-stamp sticker (riso signature) */}
        <div
          style={{
            background: ink,
            color: bg,
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            padding: "5px 10px",
            transform: "rotate(-2deg)",
            marginBottom: 8,
          }}
        >
          BARAKALI BO'LSIN
        </div>

        {/* Name */}
        <div style={{ fontFamily: font, fontSize: 28, lineHeight: 1, color: ink }}>
          {name || names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TUG'ILGAN KUN · {captionDate}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = m[3];
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  return `${day} ${months[month - 1] ?? ""} ${year}`;
}
